import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Link,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useSnackbar } from 'notistack';
import storeProvisioningService, { StoreProvisioning } from '../../services/storeProvisioningService';
import { getApiErrorMessage } from '../../utils/apiError';

const ConfiguracionTienda: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openingAdmin, setOpeningAdmin] = useState(false);
  const [data, setData] = useState<StoreProvisioning | null>(null);
  const [domain, setDomain] = useState('');
  const [storeEnabled, setStoreEnabled] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const provisioning = await storeProvisioningService.getCurrent();
      setData(provisioning);
      setDomain(provisioning.domain ?? '');
      setStoreEnabled(provisioning.storeEnabled);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar la configuración de tienda'), {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const updated = await storeProvisioningService.updateCurrent({
        domain: domain.trim() || undefined,
        storeEnabled,
      });
      setData(updated);
      setDomain(updated.domain ?? '');
      setStoreEnabled(updated.storeEnabled);
      enqueueSnackbar('Configuración de tienda guardada', { variant: 'success' });
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al guardar'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenAdmin = async () => {
    if (!data?.adminUrl) {
      enqueueSnackbar('Configure un dominio y active la tienda para abrir la administración', {
        variant: 'warning',
      });
      return;
    }

    try {
      setOpeningAdmin(true);
      const handoff = await storeProvisioningService.createAdminHandoff();
      const callbackUrl = new URL(`${data.adminUrl.replace(/\/admin\/?$/, '')}/admin/auth/callback`);
      callbackUrl.searchParams.set('token', handoff.accessToken);
      window.open(callbackUrl.toString(), '_blank', 'noopener,noreferrer');
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo abrir la administración de tienda'), {
        variant: 'error',
      });
    } finally {
      setOpeningAdmin(false);
    }
  };

  const statusLabel = !storeEnabled
    ? 'Inactiva'
    : domain.trim() || data?.suggestedSubdomain
      ? 'Activa'
      : 'Activa (dominio pendiente)';

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <StorefrontIcon color="primary" />
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Tienda online
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Active la tienda y configure el dominio público. La apariencia, layout y catálogo se administran desde el panel de la tienda.
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={3} maxWidth={720}>
              <Alert severity={storeEnabled ? 'success' : 'info'}>
                Estado: <strong>{statusLabel}</strong>
                {data?.tenantCode ? ` · Tenant ${data.tenantCode}` : ''}
              </Alert>

              <FormControlLabel
                control={
                  <Switch
                    checked={storeEnabled}
                    onChange={(e) => setStoreEnabled(e.target.checked)}
                    disabled={saving}
                  />
                }
                label="Tienda habilitada"
              />

              <TextField
                label="Dominio"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                fullWidth
                disabled={saving}
                helperText={
                  data?.suggestedSubdomain
                    ? `Sugerido: ${data.suggestedSubdomain}`
                    : 'Dominio custom o subdominio de la tienda'
                }
                inputProps={{ maxLength: 255 }}
              />

              {data?.storeUrl && (
                <Typography variant="body2" color="text.secondary">
                  URL pública:{' '}
                  <Link href={data.storeUrl} target="_blank" rel="noopener noreferrer">
                    {data.storeUrl}
                  </Link>
                </Typography>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                  {saving ? 'Guardando...' : 'Guardar'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  onClick={handleOpenAdmin}
                  disabled={openingAdmin || !storeEnabled}
                >
                  {openingAdmin ? 'Abriendo...' : 'Administrar tienda'}
                </Button>
              </Stack>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ConfiguracionTienda;
